import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const links = [
  { to: "/", label: "Home", testId: "nav-home" },
  { to: "/about", label: "About", testId: "nav-about" },
  { to: "/projects", label: "Projects", testId: "nav-projects" },
  { to: "/governance", label: "Governance", testId: "nav-governance" },
  { to: "/investor-relations", label: "Investor Relations", testId: "nav-investor-relations" },
  { to: "/contact", label: "Contact", testId: "nav-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 bg-white/95 backdrop-blur-sm transition-all ${
        scrolled ? "border-b border-[#0A1F44]/12" : "border-b border-transparent"
      }`}
    >
      <div className="evg-container">
        <div className="flex items-center justify-between h-20 md:h-24">
          <Logo testId="navbar-logo" />

          <nav className="hidden lg:flex items-center gap-10" aria-label="Primary">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.testId}
                className={({ isActive }) =>
                  `text-[11px] uppercase tracking-[0.24em] font-medium transition-colors ${
                    isActive
                      ? "text-[#0A1F44]"
                      : "text-[#0A1F44]/55 hover:text-[#0A1F44]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/investor-relations"
              data-testid="navbar-cta-investment"
              className="text-[11px] uppercase tracking-[0.24em] font-medium text-[#0A1F44] border-b border-[#D4AF37] pb-1 hover:text-[#D4AF37] transition-colors"
            >
              Discuss Investment
            </Link>
          </div>

          <button
            type="button"
            data-testid="navbar-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-[#0A1F44]"
            aria-label="Toggle navigation"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[#0A1F44]/10 bg-white">
          <div className="evg-container py-6 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-${l.testId}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.2em] py-4 border-b border-[#0A1F44]/8 ${
                    isActive ? "text-[#0A1F44] font-medium" : "text-[#0A1F44]/65"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/investor-relations"
              data-testid="mobile-cta-investment"
              className="mt-6 text-[11px] uppercase tracking-[0.24em] font-medium text-[#0A1F44] border-b border-[#D4AF37] pb-2 self-start"
            >
              Discuss Investment
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
